import React, { useState, useEffect, useCallback, useRef } from 'react';
import { baseUrl, assetPrimaryLabel, assetFullLabel } from '../../shared';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Autocomplete from '@mui/material/Autocomplete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { StatusChip, Toggle } from '../../components/ui';
import QrScanDialog from '../../components/pwa/QrScanDialog';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import {
  getCachedVisit, getCachedAuditTypesForItem, getCachedChecklist,
  saveInspectionToOutbox, getPendingInspectionCount,
} from '../../offline/capture';
import { isVisitOffline, listOfflineVisits } from '../../offline/takeOffline';
import { setFormOpen } from '../../offline/reloadGuard';
import SyncButton from '../../components/pwa/SyncButton';

// ScoreToggle fill styles (Direction C) — solid token fill when active, neutral surface when off.
const scoreSx = (tone) => ({
  fontSize: { xs: 13, sm: 11 },
  fontWeight: 700,
  px: { xs: 2.5, sm: 1.5 },
  py: { xs: 1.25, sm: 0.5 },
  flex: { xs: 1, sm: 'none' },
  border: 'none',
  color: 'var(--muted)',
  background: 'var(--bg)',
  '&.Mui-selected': { bgcolor: `var(--${tone})`, color: '#fff', '&:hover': { bgcolor: `var(--${tone})` } },
});

dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});


// enabled gates the fetch: InspectionForm stays permanently mounted (closed)
// inside assetView, so fetching on mount fired a pointless audit-types request
// (a 401 for customer users) on every asset lookup. Fetches once, on first
// becoming enabled.
function useAuditTypes(enabled = true) {
  const [auditTypes, setAuditTypes] = useState([]);
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;
    fetch(baseUrl + 'inspection/audit-types', {
      headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') },
    })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setAuditTypes(d.map(r => ({ value: r.auditKey, label: r.label }))); })
      .catch(() => {});
  }, [enabled]);
  return auditTypes;
}

// ── Inspection Form ───────────────────────────────────────────────────────────

export function InspectionForm({ open, onClose, onSaved, editRecord, companies, prefillAsset, visitId }) {
  // prefillAsset: { catNo, customer, assetId, item, manufacturer, model } — skips step 1 when set
  // visitId: when set, this inspection belongs to a (possibly offline) site visit -
  //   enables offline capture (cache-backed reads + outbox write) with no signal.
  const online = useOnlineStatus();
  const offlineCapture = !online && visitId != null; // no signal + within a visit → queue locally
  const auditTypes = useAuditTypes(open);
  const auditTypeMap = Object.fromEntries(auditTypes.map(t => [t.value, t.label]));
  const prefilled = !editRecord && !!prefillAsset;
  const [step, setStep] = useState(prefilled ? 2 : 1);
  const [form, setForm] = useState({ catNo: '', customer: '', auditType: '', inspectedBy: '', inspectionDate: dayjs().format('YYYY-MM-DD'), reinspectionDue: '', notes: '', firstTimeInspected: 0 });
  const [checklist, setChecklist] = useState([]);
  const [reinspectionInterval, setReinspectionInterval] = useState(3); // months — drives reinspectionDue (shown on checklist screen, matches simple inspection)
  // No-checklist mode: the asset's item maps to no checklist, so we show a plain Pass/Fail/NTI screen
  // (replacing the deprecated simple sheet). manualResult holds the operator's PASS/FAIL choice.
  const [noChecklist, setNoChecklist] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [assetStatus, setAssetStatus] = useState(null); // hire status info (only present for hire items)
  const [customerAssets, setCustomerAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mappedAuditTypes, setMappedAuditTypes] = useState(null); // null = show all
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Block a service-worker update from reloading the page while this form is
  // open (§7/§10: never reload mid-inspection).
  useEffect(() => {
    if (!open) return undefined;
    setFormOpen(true);
    return () => setFormOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setAssetStatus(null);
    setNoChecklist(false);
    setManualResult(null);
    if (editRecord) {
      setForm({
        catNo: editRecord.catNo || '',
        customer: editRecord.customer || '',
        auditType: editRecord.auditType || '',
        inspectedBy: editRecord.inspectedBy || localStorage.getItem('username') || '',
        inspectionDate: editRecord.inspectionDate ? dayjs(editRecord.inspectionDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        reinspectionDue: editRecord.reinspectionDue ? dayjs(editRecord.reinspectionDue).format('YYYY-MM-DD') : '',
        notes: editRecord.notes || '',
        firstTimeInspected: Number(editRecord.firstTimeInspected) === 1 ? 1 : 0,
      });
      // Derive the interval from the saved dates, snapped to the nearest option (fallback 3mo)
      const months = (editRecord.inspectionDate && editRecord.reinspectionDue)
        ? dayjs(editRecord.reinspectionDue).diff(dayjs(editRecord.inspectionDate), 'month')
        : 3;
      setReinspectionInterval([3, 6, 9, 12].reduce((a, b) => Math.abs(b - months) < Math.abs(a - months) ? b : a, 3));
      setChecklist(editRecord.checklist || []);
      // A saved record with no checklist is a no-checklist (Pass/Fail/NTI) inspection — restore that mode
      if (!editRecord.checklist || editRecord.checklist.length === 0) {
        setNoChecklist(true);
        setManualResult(editRecord.overallResult === 'FAIL' ? 'FAIL' : editRecord.overallResult === 'PASS' ? 'PASS' : null);
        setStep(2);
      } else {
        setStep(2);
      }
      setCustomerAssets([]);
    } else if (prefillAsset) {
      setForm({ catNo: prefillAsset.catNo || '', customer: prefillAsset.customer || '', auditType: '', inspectedBy: localStorage.getItem('username') || '', inspectionDate: dayjs().format('YYYY-MM-DD'), reinspectionDue: '', notes: '', firstTimeInspected: Number(prefillAsset.firstTimeInspected) === 1 ? 1 : 0 });
      setReinspectionInterval(3);
      setChecklist([]);
      setCustomerAssets([]);
      setStep(1); // auto-advances to step 2 below when the item maps to a single checklist (or to no-checklist mode when none map)
    } else {
      setForm({ catNo: '', customer: '', auditType: '', inspectedBy: localStorage.getItem('username') || '', inspectionDate: dayjs().format('YYYY-MM-DD'), reinspectionDue: '', notes: '', firstTimeInspected: 0 });
      setReinspectionInterval(3);
      setChecklist([]);
      setCustomerAssets([]);
      setStep(1);
    }
    setError('');
    setMappedAuditTypes(null);
  }, [open, editRecord, prefillAsset]);

  // Reinspection due is computed from inspection date + selected interval (matches the simple inspection; backend stores it as-is).
  // A failed inspection gets no future reinspection date (mirrors the simple, which drops Next Inspection Due on Fail/NTI).
  useEffect(() => {
    if (!form.inspectionDate) return;
    const allAns = checklist.length > 0 && checklist.every(c => c.result != null);
    const failed = noChecklist
      ? manualResult === 'FAIL'
      : (allAns && checklist.some(c => c.result === 'FAIL'));
    const due = failed
      ? form.inspectionDate
      : dayjs(form.inspectionDate).add(reinspectionInterval, 'month').format('YYYY-MM-DD');
    setForm(f => ({ ...f, reinspectionDue: due }));
  }, [form.inspectionDate, reinspectionInterval, checklist, noChecklist, manualResult]);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const loadInspectionTypeMap = async (assetType, autoAdvance = false) => {
    if (!assetType) { setMappedAuditTypes(null); return; }
    try {
      // Offline within a visit → resolve item→auditType from the cached type map.
      let mapped;
      if (offlineCapture) {
        mapped = await getCachedAuditTypesForItem(assetType);
      } else {
        const res = await fetch(baseUrl + 'workshop/settings/inspection-type-map', { headers: authHeaders() });
        const rows = await res.json();
        if (!Array.isArray(rows)) { setMappedAuditTypes(null); return; }
        mapped = rows.filter(r => r.item === assetType).map(r => r.auditType);
      }
      setMappedAuditTypes(mapped.length > 0 ? mapped : null);
      if (mapped.length === 1) {
        // Single checklist for this item — no need to ask; pick it and (when prefilled) skip straight to the checklist
        setNoChecklist(false);
        setForm(f => ({ ...f, auditType: mapped[0] }));
        await loadChecklist(mapped[0]);
        if (autoAdvance) setStep(2);
      } else if (mapped.length === 0 && autoAdvance) {
        // No checklist maps to this item — open the no-checklist Pass/Fail/NTI screen (deprecated simple sheet replacement)
        setNoChecklist(true);
        setChecklist([]);
        setForm(f => ({ ...f, auditType: '' }));
        setStep(2);
      }
    } catch { setMappedAuditTypes(null); }
  };

  // Auto-load inspection type mapping when prefillAsset has a type (auto-advance past step 1 if only one checklist applies)
  useEffect(() => {
    if (!open || editRecord || !prefillAsset?.assetType) return;
    loadInspectionTypeMap(prefillAsset.assetType, true);
  }, [open, prefillAsset?.assetType]);

  // Load hire status (Asset Status / notes) for the selected asset — only hire items return data
  useEffect(() => {
    if (!open || !form.catNo) { return; }
    let cancelled = false;
    fetch(baseUrl + 'hireStatus/' + form.catNo, { headers: { ...authHeaders(), catNo: form.catNo } })
      .then(r => r.json())
      .then(d => { if (!cancelled) setAssetStatus(Array.isArray(d) ? d[0] : d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, form.catNo]);

  const handleCustomerChange = async (newCustomer) => {
    setForm(f => ({ ...f, customer: newCustomer || '', catNo: '' }));
    setCustomerAssets([]);
    if (!newCustomer) return;
    setAssetsLoading(true);
    try {
      const res = await fetch(baseUrl + 'asset/customer/' + encodeURIComponent(newCustomer), { headers: authHeaders() });
      const data = await res.json();
      setCustomerAssets(Array.isArray(data) ? data : []);
    } finally { setAssetsLoading(false); }
  };

  const loadChecklist = async (auditType) => {
    // Offline within a visit → read the pre-cached checklist template.
    if (offlineCapture) {
      setChecklist(await getCachedChecklist(auditType));
      return;
    }
    const res = await fetch(baseUrl + 'inspection/checklist/' + auditType, { headers: authHeaders() });
    const items = await res.json();
    setChecklist(items);
  };

  const handleTypeChange = async (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, auditType: val }));
    if (val) await loadChecklist(val);
  };

  const handleNext = async () => {
    if (!prefillAsset) {
      if (!form.customer) { setError('Select a customer.'); return; }
      if (!form.catNo) { setError('Select an asset.'); return; }
    }
    if (!form.auditType) { setError('Select an audit type.'); return; }
    if (checklist.length === 0) await loadChecklist(form.auditType);
    setError('');
    setStep(2);
  };

  const setResult = (idx, result) => {
    setChecklist(prev => prev.map((item, i) => i === idx ? { ...item, result } : item));
  };

  // Quick bulk set — mark every checklist item PASS/FAIL, then the user can tweak individual rows below.
  const setAllResults = (result) => {
    setChecklist(prev => prev.map(item => ({ ...item, result })));
  };

  const scored = checklist.filter(c => c.result && c.result !== 'N/A');
  const passed = checklist.filter(c => c.result === 'PASS').length;
  const total = checklist.filter(c => c.result !== 'N/A').length;
  const achievedScore = total > 0 ? (passed / total) * 100 : null;
  const hasAnyFail = checklist.some(c => c.result === 'FAIL');
  const anyAnswered = checklist.some(c => c.result !== null && c.result !== undefined);
  const allAnswered = checklist.length > 0 && checklist.every(c => c.result !== null && c.result !== undefined);
  // In no-checklist mode the result is the operator's manual Pass/Fail choice (NTI is saved via its own button).
  const overallResult = noChecklist ? manualResult : (allAnswered ? (hasAnyFail ? 'FAIL' : 'PASS') : null);
  const canSave = noChecklist ? !!manualResult : allAnswered;

  const handleSave = async (resultOverride) => {
    setSaving(true);
    try {
      // NTI: record the inspection as Not To Inspect — result is forced, no score, and no future reinspection date (like the simple)
      const isNti = resultOverride === 'NTI';
      const body = {
        ...form,
        checklist,
        achievedScore: isNti ? null : (achievedScore != null ? parseFloat(achievedScore.toFixed(2)) : null),
        overallResult: isNti ? 'NTI' : overallResult,
        reinspectionDue: isNti ? form.inspectionDate : form.reinspectionDue,
      };

      // Offline capture: queue to the outbox instead of hitting the API. Field
      // inspections are create-only offline (editing a synced record needs a
      // connection - playbook §11), so editRecord always goes to the server.
      if (offlineCapture && !editRecord) {
        await saveInspectionToOutbox({ visitId, payload: body });
        onSaved();
        return;
      }
      if (!online) {
        setError(editRecord
          ? "You're offline. Reconnect to edit this inspection."
          : "You're offline. Reconnect, or take this visit offline first, to save.");
        return;
      }

      const url = editRecord
        ? baseUrl + 'inspection/results/' + editRecord.id
        : baseUrl + 'inspection/results';
      const res = await fetch(url, {
        method: editRecord ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved();
      else setError('Failed to save.');
    } catch (e) {
      setError('Failed to save.' + (e && e.message ? ' ' + e.message : ''));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton size="small" onClick={onClose} sx={{ mr: 0.5 }}>
              <CloseIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, fontSize: { xs: 16, sm: 20 } }}>
            {editRecord ? 'Edit Inspection' : noChecklist ? 'Quick Inspection (Pass / Fail)' : 'New Asset Inspection'}
          </Typography>
          {overallResult && (
            <StatusChip domain="inspection" value={overallResult} dot size="md" />
          )}
          {achievedScore != null && !isMobile && (
            <Typography variant="body2" color="text.secondary">Score: {achievedScore.toFixed(1)}%</Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              Asset & Inspection Details
            </Typography>
            <Divider />

            {prefillAsset ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <strong>{assetFullLabel(prefillAsset)}</strong>{' '}
                {[prefillAsset.customer, prefillAsset.manufacturer, prefillAsset.model, prefillAsset.item].filter(Boolean).join(' · ')}
              </Alert>
            ) : (
              <>
                <Autocomplete
                  size="small" fullWidth
                  options={(companies || []).map(c => c.company)}
                  value={form.customer || null}
                  onChange={(_, val) => handleCustomerChange(val)}
                  renderInput={(params) => <TextField {...params} label="Customer" />}
                />

                <Autocomplete
                  size="small" fullWidth
                  disabled={!form.customer}
                  loading={assetsLoading}
                  options={customerAssets}
                  getOptionLabel={a => (a.assetId || a.catNo) ? `${assetFullLabel(a)}${a.item ? ` — ${a.item}` : ''}${a.model ? ` ${a.model}` : ''}` : ''}
                  value={customerAssets.find(a => a.catNo === form.catNo) || null}
                  onChange={(_, asset) => {
                    setForm(f => ({ ...f, catNo: asset ? asset.catNo : '', auditType: '' }));
                    if (asset?.item) loadInspectionTypeMap(asset.item);
                    else setMappedAuditTypes(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={form.customer ? 'Asset Number' : 'Select customer first'}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{assetsLoading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>) }}
                    />
                  )}
                />
                {form.catNo && customerAssets.find(a => a.catNo === form.catNo) && (() => {
                  const a = customerAssets.find(x => x.catNo === form.catNo);
                  return (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {[a.manufacturer, a.model, a.item, a.siteLocation].filter(Boolean).join(' · ')}
                    </Alert>
                  );
                })()}
              </>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>{mappedAuditTypes && mappedAuditTypes.length > 1 ? 'Select Checklist' : 'Audit Type'}</InputLabel>
              <Select value={form.auditType} label={mappedAuditTypes && mappedAuditTypes.length > 1 ? 'Select Checklist' : 'Audit Type'} onChange={handleTypeChange}>
                {(mappedAuditTypes ? auditTypes.filter(t => mappedAuditTypes.includes(t.value)) : auditTypes).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Inspection date, reinspection interval and notes are set on the checklist screen.
            </Typography>
          </Box>
        )}

        {step === 2 && (
          <Box>
            {assetStatus?.assetStatus ? (
              <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, p: 2, mb: 2, bgcolor: 'var(--info-bg)' }}>
                <Typography variant="subtitle2" color="primary" fontWeight={600}>Asset Status</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{assetStatus.assetStatus || 'N/A'}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" fontWeight={600}>Asset Status Notes</Typography>
                <Typography variant="body2" sx={{ maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>{assetStatus.assetStatusNotes || 'No notes available'}</Typography>
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2"><strong>Asset:</strong> {assetFullLabel(prefillAsset || customerAssets.find(a => a.catNo === form.catNo) || { catNo: form.catNo })}</Typography>
              <Typography variant="body2"><strong>Customer:</strong> {form.customer || '—'}</Typography>
              {!noChecklist && <Typography variant="body2"><strong>Type:</strong> {auditTypeMap[form.auditType]}</Typography>}
              <TextField size="small" type="date" label="Inspection Date" value={form.inspectionDate} onChange={set('inspectionDate')} InputLabelProps={{ shrink: true }} sx={{ width: 165 }} />
            </Box>
            {achievedScore != null && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={achievedScore}
                  sx={{ height: 8, borderRadius: 4, bgcolor: 'var(--fail-bg)', '& .MuiLinearProgress-bar': { bgcolor: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)' } }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                  {passed}/{total} items passed — {achievedScore.toFixed(1)}%
                </Typography>
              </Box>
            )}
            {noChecklist && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 2, sm: 3 }, mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)' }}>
                  Inspection Result
                </Typography>
                <ToggleButtonGroup
                  value={manualResult || ''}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setManualResult(v); }}
                >
                  <ToggleButton value="PASS" sx={{ ...scoreSx('pass'), px: { xs: 4, sm: 5 }, py: 1 }}>PASS</ToggleButton>
                  <ToggleButton value="FAIL" sx={{ ...scoreSx('fail'), px: { xs: 4, sm: 5 }, py: 1 }}>FAIL</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {!noChecklist && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, bgcolor: 'var(--surface-2)', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, width: 28 }}>#</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Checklist Item</Typography>
                <Box sx={{ width: { xs: 'auto', sm: 220 }, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5, color: 'var(--muted)' }}>Set all:</Typography>
                  <Button size="small" disabled={checklist.length === 0} onClick={() => setAllResults('PASS')} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11, fontWeight: 700, lineHeight: 1.4, color: 'var(--pass)' }}>Pass</Button>
                  <Button size="small" disabled={checklist.length === 0} onClick={() => setAllResults('FAIL')} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11, fontWeight: 700, lineHeight: 1.4, color: 'var(--fail)' }}>Fail</Button>
                </Box>
              </Box>
              {checklist.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 0 },
                    px: 1.5,
                    py: { xs: 1.5, sm: 1 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: item.result === 'PASS' ? 'var(--pass-bg)' : item.result === 'FAIL' ? 'var(--fail-bg)' : undefined,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 24, flexShrink: 0 }}>{idx + 1}.</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: 14, sm: 13 } }}>{item.item}</Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={item.result || ''}
                    exclusive
                    onChange={(_, v) => { if (v !== null) setResult(idx, v); }}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                  >
                    <ToggleButton value="PASS" sx={scoreSx('pass')}>PASS</ToggleButton>
                    <ToggleButton value="FAIL" sx={scoreSx('fail')}>FAIL</ToggleButton>
                    <ToggleButton value="N/A" sx={scoreSx('neutral')}>N/A</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ))}
            </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <TextField size="small" fullWidth multiline rows={2} label="Notes / Observations" value={form.notes} onChange={set('notes')} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, px: { xs: 2, sm: 1 }, pb: { xs: 2, sm: 1 } }}>
        {step === 1 && (
          <Button
            variant="contained" onClick={handleNext}
            disabled={prefillAsset ? !form.auditType : (!form.catNo || !form.auditType)}
            fullWidth={isMobile} size={isMobile ? 'large' : 'medium'}
          >
            Next — Checklist
          </Button>
        )}
        {step === 2 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
            <Typography variant="body2" sx={{ color: 'var(--muted)', fontWeight: 600 }}>First-time inspection</Typography>
            <Toggle
              checked={Number(form.firstTimeInspected) === 1}
              onChange={() => setForm(f => ({ ...f, firstTimeInspected: Number(f.firstTimeInspected) === 1 ? 0 : 1 }))}
              ariaLabel="First-time inspection"
            />
          </Box>
        )}
        {step === 2 && overallResult === 'PASS' && (
          <TextField
            select size="small" label="Reinspection" value={reinspectionInterval}
            onChange={(e) => setReinspectionInterval(parseInt(e.target.value, 10))}
            sx={{ width: { xs: '100%', sm: 150 }, mr: { sm: 1 } }}
          >
            {[3, 6, 9, 12].map(m => <MenuItem key={m} value={m}>{m} Months</MenuItem>)}
          </TextField>
        )}
        {step === 2 && !anyAnswered && (
          <Button
            variant="outlined" color="warning" onClick={() => handleSave('NTI')}
            disabled={saving}
            fullWidth={isMobile} size={isMobile ? 'large' : 'medium'}
            sx={{ mr: { sm: 1 } }}
          >
            Save as NTI
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained" onClick={() => handleSave()}
            disabled={saving || !canSave}
            fullWidth={isMobile} size={isMobile ? 'large' : 'medium'}
          >
            {saving ? <CircularProgress size={20} /> : editRecord ? 'Save Changes' : 'Save Inspection'}
          </Button>
        )}
        {!isMobile && <Button onClick={onClose} disabled={saving}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}

// ── Inspection Detail Modal (read-only, for asset history view) ──────────────

const AUDIT_TYPE_LABELS = {
  LIFTING: 'Lifting Equipment (Jacks & Stands)',
  TOOLING: 'Hydraulic/Pneumatic Tooling',
  TORQUE_TOOL: 'Torque Tool',
  ACCESS_SYSTEM: 'Access System (Ladders)',
  ESCAPE_SYSTEM: 'Escape System',
};

export function InspectionDetailModal({ open, onClose, inspectionId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !inspectionId) return;
    setLoading(true);
    fetch(baseUrl + 'inspection/results/' + inspectionId, {
      headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, inspectionId]);

  const checklist = data?.checklist || [];
  const passed = checklist.filter(c => c.result === 'PASS').length;
  const total = checklist.filter(c => c.result !== 'N/A').length;
  const score = total > 0 ? ((passed / total) * 100).toFixed(1) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Inspection Checklist</Typography>
          {data?.overallResult && (
            <StatusChip domain="inspection" value={data.overallResult} dot size="md" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {!loading && (!data || !data.id) && (
          <Typography color="text.secondary" sx={{ py: 2 }}>This inspection record is no longer available.</Typography>
        )}
        {!loading && data && data.id && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Typography variant="body2"><strong>Asset:</strong> {assetFullLabel(data)}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {AUDIT_TYPE_LABELS[data.auditType] || data.auditType}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {data.inspectionDate ? dayjs(data.inspectionDate).format('DD/MM/YYYY') : '—'}</Typography>
              <Typography variant="body2"><strong>Inspector:</strong> {data.inspectedBy || '—'}</Typography>
              {score != null && <Typography variant="body2"><strong>Score:</strong> {score}%</Typography>}
            </Box>
            {score != null && (
              <LinearProgress
                variant="determinate" value={parseFloat(score)}
                sx={{ height: 8, borderRadius: 4, bgcolor: '#ffcdd2', '& .MuiLinearProgress-bar': { bgcolor: data.overallResult === 'PASS' ? '#2e7d32' : '#c62828' } }}
              />
            )}
            {data.notes && <Typography variant="body2" color="text.secondary"><em>{data.notes}</em></Typography>}
            <Divider />
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {checklist.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 1,
                    borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: item.result === 'PASS' ? 'var(--pass-bg)' : item.result === 'FAIL' ? 'var(--fail-bg)' : undefined,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 24, flexShrink: 0 }}>{idx + 1}.</Typography>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>{item.item}</Typography>
                  </Box>
                  <Box sx={{ ml: 1, flexShrink: 0 }}>
                    {item.result
                      ? <StatusChip domain="inspection" value={item.result} />
                      : <Box component="span" sx={{ color: 'var(--faint)', fontSize: 12 }}>—</Box>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Missing Asset Coverage Dialog ─────────────────────────────────────────────

function MissingAssetsDialog({ open, onClose, companies, auditTypeMap }) {
  const [customer, setCustomer] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [coverage, setCoverage] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ customer, fromDate, toDate });
      const res = await fetch(baseUrl + 'inspection/asset-coverage?' + params, { headers: authHeaders() });
      const data = await res.json();
      setCoverage(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  const uninspected = coverage.filter(a => !a.inspected);
  const inspected = coverage.filter(a => a.inspected);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Missing Asset Report — Asset Coverage Check</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Customer</InputLabel>
            <Select value={customer} label="Customer" onChange={e => setCustomer(e.target.value)}>
              <MenuItem value=""><em>Select customer…</em></MenuItem>
              {companies.map(c => <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="From" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To" type="date" value={toDate} onChange={e => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="outlined" onClick={load} disabled={!customer || loading}>
            {loading ? <CircularProgress size={18} /> : 'Check Coverage'}
          </Button>
        </Box>

        {coverage.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={`${coverage.length} total assets`} variant="outlined" />
              <Chip label={`${inspected.length} inspected`} sx={{ bgcolor: 'var(--pass-bg)', color: 'var(--pass)' }} />
              <Chip label={`${uninspected.length} not inspected`} sx={{ bgcolor: uninspected.length > 0 ? 'var(--fail-bg)' : 'var(--neutral-bg)', color: uninspected.length > 0 ? 'var(--fail)' : 'var(--neutral)' }} />
            </Box>

            {uninspected.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#c62828', fontSize: 11 }}>
                  Not Inspected ({uninspected.length})
                </Typography>
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'var(--fail-bg)' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Asset No</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Manufacturer</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Site</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uninspected.map(a => (
                      <TableRow key={a.catNo}>
                        <TableCell sx={{ fontSize: 12 }}>{assetPrimaryLabel(a)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{a.item || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{a.manufacturer || '—'} {a.model || ''}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{a.siteLocation || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {inspected.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#2e7d32', fontSize: 11 }}>
                  Inspected ({inspected.length})
                </Typography>
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f1f8e9' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Asset No</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Audit Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inspected.map(a => (
                      <TableRow key={a.catNo}>
                        <TableCell sx={{ fontSize: 12 }}>{assetPrimaryLabel(a)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{a.item || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{auditTypeMap[a.lastInspection?.auditType] || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{a.lastInspection?.inspectionDate ? dayjs(a.lastInspection.inspectionDate).format('DD/MM/YYYY') : '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {a.lastInspection?.overallResult && (
                            <StatusChip domain="inspection" value={a.lastInspection.overallResult} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Audit Summary Dialog ──────────────────────────────────────────────────────

function AuditSummaryDialog({ open, onClose, companies, auditTypeMap }) {
  const [customer, setCustomer] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fromDate, toDate });
      if (customer) params.append('customer', customer);
      const res = await fetch(baseUrl + 'inspection/results?' + params, { headers: authHeaders() });
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  const passed = results.filter(r => r.overallResult === 'PASS').length;
  const failed = results.filter(r => r.overallResult === 'FAIL').length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Audit Summary Report</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Customer (optional)</InputLabel>
            <Select value={customer} label="Customer (optional)" onChange={e => setCustomer(e.target.value)}>
              <MenuItem value=""><em>All Customers</em></MenuItem>
              {companies.map(c => <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="From" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To" type="date" value={toDate} onChange={e => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="outlined" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Generate Summary'}
          </Button>
        </Box>

        {results.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={`${results.length} inspections`} variant="outlined" />
              <Chip label={`${passed} pass`} sx={{ bgcolor: 'var(--pass-bg)', color: 'var(--pass)', fontWeight: 700 }} />
              <Chip label={`${failed} fail`} sx={{ bgcolor: failed > 0 ? 'var(--fail-bg)' : 'var(--neutral-bg)', color: failed > 0 ? 'var(--fail)' : 'var(--neutral)', fontWeight: 700 }} />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Copy the table below to include in an email report.
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
                  {['Asset No', 'Customer', 'Audit Type', 'Inspector', 'Date', 'Score', 'Result', 'Reinspection Due'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(r => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontSize: 12 }}>{assetPrimaryLabel(r) || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.customer || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{auditTypeMap[r.auditType] || r.auditType}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.inspectedBy || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.inspectionDate ? dayjs(r.inspectionDate).format('DD/MM/YYYY') : '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.achievedScore != null ? Number(r.achievedScore).toFixed(1) + '%' : '—'}</TableCell>
                    <TableCell>
                      {r.overallResult && <StatusChip domain="inspection" value={r.overallResult} />}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: r.reinspectionDue && dayjs(r.reinspectionDue).isBefore(dayjs(), 'day') ? 'error.main' : undefined }}>
                      {r.reinspectionDue ? dayjs(r.reinspectionDue).format('DD/MM/YYYY') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Inspection() {
  const auditTypes = useAuditTypes();
  const auditTypeMap = Object.fromEntries(auditTypes.map(t => [t.value, t.label]));
  const [searchParams] = useSearchParams();
  const visitId = searchParams.get('visitId');
  const isAdmin = localStorage.getItem('canRegisterUser') === '1';
  const online = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [visitCachedOffline, setVisitCachedOffline] = useState(false);
  const [offlineVisits, setOfflineVisits] = useState(null); // null=loading, []=none
  // When there's no visitId in the URL, surface the visits taken offline so the
  // screen is a first-class landing (esp. offline, where the admin page can't load).
  useEffect(() => {
    if (visitId) { setOfflineVisits(null); return; }
    listOfflineVisits().then(setOfflineVisits);
  }, [visitId]);
  const refreshPending = useCallback(() => {
    getPendingInspectionCount().then(setPendingCount);
    if (visitId) isVisitOffline(Number(visitId)).then(setVisitCachedOffline);
    else setVisitCachedOffline(false);
  }, [visitId]);
  useEffect(() => { refreshPending(); }, [refreshPending, visitId]);

  const [results, setResults] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [missingOpen, setMissingOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Booking inspection mode state (visitId in URL)
  const [visitData, setVisitData] = useState(null);
  const [visitLoading, setVisitLoading] = useState(false);
  const [bookingInspectAsset, setBookingInspectAsset] = useState(null);
  const [assetFilter, setAssetFilter] = useState(''); // filters the booking asset list by Asset ID / Cat No
  const [scanOpen, setScanOpen] = useState(false); // in-app QR scanner for scan-to-filter

  // Booking filter (admin dropdown on main results page)
  const [visits, setVisits] = useState([]);
  const [filterVisit, setFilterVisit] = useState('');
  const [visitAssets, setVisitAssets] = useState(null);
  const [visitStartDate, setVisitStartDate] = useState(null);
  const [visitAssetsLoading, setVisitAssetsLoading] = useState(false);

  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [pdfUrl, setPdfUrl] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterCustomer) params.append('customer', filterCustomer);
    if (filterType) params.append('auditType', filterType);
    if (filterResult) params.append('result', filterResult);
    if (filterFrom) params.append('fromDate', filterFrom);
    if (filterTo) params.append('toDate', filterTo);
    fetch(baseUrl + 'inspection/results?' + params, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setResults(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterCustomer, filterType, filterResult, filterFrom, filterTo]);

  useEffect(() => {
    load();
    fetch(baseUrl + 'company/list', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    fetch(baseUrl + 'schedule/visits', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setVisits(Array.isArray(d) ? d.filter(v => v.asset_count > 0) : []));
  }, [isAdmin]);

  useEffect(() => {
    if (!filterVisit) { setVisitAssets(null); setVisitStartDate(null); return; }
    setVisitAssetsLoading(true);
    fetch(baseUrl + 'schedule/visits/' + filterVisit, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setVisitAssets(d.assets || []); setVisitStartDate(d.start_date || null); setVisitAssetsLoading(false); })
      .catch(() => setVisitAssetsLoading(false));
  }, [filterVisit]);

  useEffect(() => {
    if (!visitId) { setVisitData(null); return; }
    let cancelled = false;
    setVisitLoading(true);
    const vid = Number(visitId);
    const loadFromCache = async () => {
      const cached = await getCachedVisit(vid);
      if (!cancelled) { setVisitData(cached); setVisitLoading(false); }
    };
    if (!online) {
      // No signal → render from the cached (taken-offline) pool.
      loadFromCache();
    } else {
      fetch(baseUrl + 'schedule/visits/' + visitId, { headers: authHeaders() })
        .then(r => r.json())
        .then(visitD => { if (!cancelled) { setVisitData(visitD); setVisitLoading(false); } })
        .catch(() => { if (!cancelled) loadFromCache(); }); // server unreachable → fall back to cache
    }
    return () => { cancelled = true; };
  }, [visitId, online]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inspection record?')) return;
    await fetch(baseUrl + 'inspection/results/' + id, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const handleGeneratePdf = async (r) => {
    setGeneratingPdf(r.id);
    try {
      const res = await fetch(baseUrl + 'inspection/results/' + r.id + '/report', {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (data.proxyUrl) {
        setPdfUrl(prev => ({ ...prev, [r.id]: baseUrl + data.proxyUrl }));
        const pdfRes = await fetch(baseUrl + data.proxyUrl, { headers: authHeaders() });
        const blob = await pdfRes.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.fileName || 'Inspection Report.pdf';
        a.click();
        window.open(url, '_blank');
      }
    } finally { setGeneratingPdf(null); }
  };

  const overdueCount = results.filter(r => r.reinspectionDue && dayjs(r.reinspectionDue).isBefore(dayjs(), 'day')).length;
  const hasFilters = filterCustomer || filterType || filterResult || filterFrom || filterTo || filterVisit;

  // ── Booking inspection mode ──────────────────────────────────────────────────
  if (visitId) {
    if (visitLoading) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!visitData) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error">Visit not found or you are not assigned to it.</Typography>
        </Box>
      );
    }

    const assets = visitData.assets || [];
    const q = assetFilter.trim().toLowerCase();
    const filteredAssets = q
      ? assets.filter(a =>
          (a.assetId || '').toLowerCase().includes(q) ||
          (a.cat_no || '').toLowerCase().includes(q))
      : assets;
    const canAnyInspect = localStorage.getItem('canInspectAsset') === '1' || localStorage.getItem('canUseDetailedInspection') === '1';

    // After a save, refresh the list. Offline (or if the server is unreachable)
    // read the cached pool so the just-queued inspection shows as done-by-me.
    const reloadVisit = () => {
      refreshPending();
      if (!online) return getCachedVisit(Number(visitId)).then(setVisitData);
      return fetch(baseUrl + 'schedule/visits/' + visitId, { headers: authHeaders() })
        .then(r => r.json()).then(d => setVisitData(d))
        .catch(() => getCachedVisit(Number(visitId)).then(setVisitData));
    };

    // All inspections now go through the advanced InspectionForm — it shows a checklist when the
    // asset's item maps to one, or a no-checklist Pass/Fail/NTI screen when none map.
    const handleAssetClick = (asset) => setBookingInspectAsset(asset);

    // Per-item state (§8: Outstanding / Done by me / Done by teammate X on date).
    // Prefer explicit offline/sync fields (completedThisVisit + inspectedBy);
    // fall back to deriving "done this visit" from status + a last-inspection
    // date on/after the visit start (the online visit endpoint omits the flags).
    const meUser = (localStorage.getItem('username') || '').toLowerCase();
    const visitStart = visitData.start_date ? dayjs(visitData.start_date) : null;
    const assetState = (a) => {
      let done = !!a.completedThisVisit;
      if (!done && a.inspectionStatus && a.lastInspectionDate && visitStart) {
        const d = dayjs(a.lastInspectionDate, ['DD/MM/YYYY', 'YYYY-MM-DD']);
        done = d.isValid() && d.isSameOrAfter(visitStart, 'day');
      }
      if (!done) return { done: false, label: 'Outstanding', tone: 'neutral' };
      const by = a.inspectedBy || null;
      if (by && by.toLowerCase() === meUser) return { done: true, label: 'Done by you', tone: 'me', by, status: a.inspectionStatus };
      if (by) return { done: true, label: `Done by ${by}`, tone: 'other', by, status: a.inspectionStatus };
      return { done: true, label: 'Done', tone: 'done', status: a.inspectionStatus };
    };
    const doneCount = assets.filter(a => assetState(a).done).length;

    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => window.history.back()} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{visitData.company}</Typography>
            <Typography variant="body2" color="text.secondary">
              {(visitData.sites || []).join(', ') || visitData.site_locations || ''}
              {visitData.start_date ? ` · ${dayjs(visitData.start_date).format('DD/MM/YYYY')}` : ''}
            </Typography>
          </Box>
          {!online && (
            <Chip size="small" icon={<CloudOffIcon />} label="Offline" color="warning" variant="outlined" sx={{ fontWeight: 600, alignSelf: 'flex-start' }} />
          )}
          {/* Only meaningful when this visit has offline data (taken offline
              and/or pending uploads) - hidden otherwise to avoid a no-op button. */}
          {visitCachedOffline && (
            <SyncButton
              online={online}
              pendingCount={pendingCount}
              onSynced={() => reloadVisit()}
            />
          )}
        </Box>

        {/* Persistent pending-upload indicator - so a tech never walks away
            thinking queued work has been saved to the server. Full "Sync now"
            lands in Phase 4; for now it's an honest count + gentle prompt. */}
        {pendingCount > 0 && (
          <Alert severity={online ? 'info' : 'warning'} icon={<CloudUploadIcon />} sx={{ mb: 2, '& .MuiAlert-message': { fontSize: 12.5 } }}>
            <b>{pendingCount} inspection{pendingCount === 1 ? '' : 's'} waiting to upload.</b>{' '}
            {online ? 'Syncing arrives in a later update. Keep the app installed until then.' : 'Reconnect to sync. Do not log out or clear data until you do.'}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 700 }}>
            Assets to inspect ({q ? `${filteredAssets.length} of ${assets.length}` : assets.length})
          </Typography>
          {assets.length > 0 && (
            <Chip size="small" label={`${doneCount} / ${assets.length} done`} color={doneCount === assets.length ? 'success' : 'default'} variant={doneCount === assets.length ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
          )}
        </Box>

        {assets.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'stretch' }}>
            <TextField
              size="small" fullWidth
              label="Filter by Asset ID"
              placeholder="Start typing or scan an Asset ID…"
              value={assetFilter}
              onChange={e => setAssetFilter(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: assetFilter ? (
                  <IconButton size="small" onClick={() => setAssetFilter('')} aria-label="clear filter"><CloseIcon fontSize="small" /></IconButton>
                ) : null,
              }}
              sx={{ flex: 1 }}
            />
            {/* Scan-to-filter: the decoded value is typed into the filter box (no
                navigation) - playbook §8, resolves against the in-memory pool. */}
            <IconButton
              aria-label="Scan asset QR to filter"
              onClick={() => setScanOpen(true)}
              sx={{
                flexShrink: 0, borderRadius: 'var(--r-sm, 8px)',
                border: '1px solid var(--border)', color: 'var(--primary)',
                '&:hover': { borderColor: 'var(--primary)', background: 'var(--primary-tint)' },
              }}
            >
              <QrCodeScannerIcon />
            </IconButton>
          </Box>
        )}

        {assets.length === 0 && <Typography color="text.secondary">No assets listed on this visit.</Typography>}
        {assets.length > 0 && filteredAssets.length === 0 && (
          // Connectivity-aware "not on this visit" (§8). Online: offer to open the
          // scanned/typed asset anyway; offline: can't reach it (not cached).
          online ? (
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}
              action={
                <Button size="small" onClick={() => { window.location.href = '/?assetNo=' + encodeURIComponent(assetFilter.trim()); }} sx={{ textTransform: 'none' }}>
                  Open anyway
                </Button>
              }>
              <b>“{assetFilter}” isn’t on this visit.</b> You can open it anyway to inspect it directly.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}>
              <b>“{assetFilter}” isn’t in your offline copy of this visit.</b> It may have been added to the visit after you took it offline. Reconnect and sync to check. Either way it can’t be inspected while offline.
            </Alert>
          )
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredAssets.map(asset => {
            const st = assetState(asset);
            const stampColor = st.tone === 'me' ? 'var(--pass, #2e7d32)'
              : st.tone === 'other' ? 'var(--primary)'
              : st.done ? 'var(--muted)' : 'var(--warn, #92610a)';
            return (
              <Card key={asset.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `3px solid ${stampColor}` }}>
                <CardActionArea onClick={() => canAnyInspect && handleAssetClick(asset)} disabled={!canAnyInspect} sx={{ p: 0 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: '12px !important' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 15 }}>{asset.assetId || asset.cat_no || '—'}</Typography>
                        {st.done && st.status && <StatusChip domain="inspection" value={st.status} size="sm" />}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {[asset.cat_no, asset.manufacturer, asset.model, asset.item].filter(Boolean).join(' · ')}
                      </Typography>
                      {asset.siteLocation && <Typography variant="caption" color="text.secondary" display="block">{asset.siteLocation}</Typography>}
                      <Typography variant="caption" sx={{ color: stampColor, fontWeight: 600 }}>
                        {st.label}{st.done && asset.lastInspectionDate ? ` · ${asset.lastInspectionDate}` : ''}
                      </Typography>
                    </Box>
                    {canAnyInspect && (
                      <Button size="small" variant={st.done ? 'text' : 'outlined'} startIcon={<AssignmentIcon />} sx={{ flexShrink: 0 }}>
                        {st.done ? 'Re-inspect' : 'Inspect'}
                      </Button>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>

        {/* Advanced inspection form — checklist or no-checklist Pass/Fail, decided per asset */}
        <InspectionForm
          open={!!bookingInspectAsset}
          onClose={() => setBookingInspectAsset(null)}
          onSaved={() => { setBookingInspectAsset(null); reloadVisit(); }}
          companies={[]}
          visitId={visitId ? Number(visitId) : null}
          prefillAsset={bookingInspectAsset ? {
            catNo: bookingInspectAsset.cat_no, customer: visitData.company, assetId: '',
            item: bookingInspectAsset.item || '', manufacturer: bookingInspectAsset.manufacturer || '',
            model: bookingInspectAsset.model || '', assetType: bookingInspectAsset.item || '',
          } : null}
        />

        {/* Scan-to-filter: drop the decoded value into the filter box rather than
            navigating. Filter matches both Asset ID and Cat No, so either tag
            content resolves against the in-memory visit pool. */}
        <QrScanDialog
          open={scanOpen}
          onClose={() => setScanOpen(false)}
          title="Scan to find asset"
          onResult={({ value }) => {
            setScanOpen(false);
            if (value) setAssetFilter(value);
          }}
        />
      </Box>
    );
  }

  // ── Offline landing / visit picker (no visitId) ──────────────────────────────
  // Offline, the admin Site Inspections page can't load (it fetches results), so
  // land on the visits taken offline instead. A single one auto-opens. Online we
  // still show the normal page below. (§8: first-class, land-able screen.)
  if (!visitId && !online) {
    if (offlineVisits === null) {
      return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    if (offlineVisits.length === 1) {
      window.location.href = '/inspection?visitId=' + offlineVisits[0].visitId;
      return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Offline inspections</Typography>
          <Chip size="small" icon={<CloudOffIcon />} label="Offline" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>
        {offlineVisits.length === 0 ? (
          <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}>
            No visits taken offline. While you have signal, open a visit in Scheduling and tap <b>Take offline</b> to inspect it here with no connection.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {offlineVisits.map(v => (
              <Card key={v.visitId} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardActionArea onClick={() => { window.location.href = '/inspection?visitId=' + v.visitId; }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 15 }}>{v.company || `Visit #${v.visitId}`}</Typography>
                      <Typography variant="body2" color="text.secondary">{v.assetCount} asset{v.assetCount === 1 ? '' : 's'} offline</Typography>
                    </Box>
                    {v.pendingCount > 0 && (
                      <Chip size="small" icon={<CloudUploadIcon />} label={`${v.pendingCount} to upload`} color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Site Inspections</Typography>
          <Typography variant="body2" color="text.secondary">Detailed asset audit with per-item checklists</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {overdueCount > 0 && (
            <Chip icon={<WarningAmberIcon />} label={`${overdueCount} reinspection overdue`} color="warning" sx={{ fontWeight: 700 }} />
          )}
          <Button variant="outlined" size="small" startIcon={<AssignmentIcon />} onClick={() => setSummaryOpen(true)}>
            Audit Summary
          </Button>
          <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setMissingOpen(true)}>
            Missing Assets
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditRecord(null); setFormOpen(true); }}>
            New Inspection
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {isAdmin && visits.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Filter by Booking</InputLabel>
            <Select value={filterVisit} label="Filter by Booking" onChange={e => setFilterVisit(e.target.value)}>
              <MenuItem value=""><em>All Inspections</em></MenuItem>
              {visits.map(v => (
                <MenuItem key={v.id} value={String(v.id)}>
                  {v.company} · {dayjs(v.start_date).format('DD/MM/YYYY')}
                  {v.site_locations ? ` · ${v.site_locations}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Customer</InputLabel>
          <Select value={filterCustomer} label="Customer" onChange={e => setFilterCustomer(e.target.value)}>
            <MenuItem value=""><em>All Customers</em></MenuItem>
            {companies.map(c => <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Audit Type</InputLabel>
          <Select value={filterType} label="Audit Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value=""><em>All Types</em></MenuItem>
            {auditTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Result</InputLabel>
          <Select value={filterResult} label="Result" onChange={e => setFilterResult(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            <MenuItem value="PASS">Pass</MenuItem>
            <MenuItem value="FAIL">Fail</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label="From" type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To" type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        {hasFilters && (
          <Button size="small" onClick={() => { setFilterCustomer(''); setFilterType(''); setFilterResult(''); setFilterFrom(''); setFilterTo(''); setFilterVisit(''); }}>
            Clear
          </Button>
        )}
      </Box>

      {filterVisit && visitAssets !== null ? (
        visitAssetsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : visitAssets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">No assets on this booking.</Typography>
          </Box>
        ) : (
          <>
            {/* Summary chips for booking view */}
            {(() => {
              const doneAssets = visitAssets.filter(a => {
                const s = a.inspectionStatus?.toUpperCase();
                return s && a.lastInspectionDate && visitStartDate &&
                  dayjs(a.lastInspectionDate, 'DD/MM/YYYY').isSameOrAfter(dayjs(visitStartDate), 'day');
              });
              const passCount = doneAssets.filter(a => a.inspectionStatus?.toUpperCase() === 'PASS').length;
              const failCount = doneAssets.filter(a => a.inspectionStatus?.toUpperCase() === 'FAIL').length;
              const notDoneCount = visitAssets.length - doneAssets.length;
              return (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${visitAssets.length} assets`} variant="outlined" size="small" />
                  <Chip label={`${passCount} pass`} size="small" sx={{ bgcolor: 'var(--pass-bg)', color: 'var(--pass)', fontWeight: 700 }} />
                  <Chip label={`${failCount} fail`} size="small" sx={{ bgcolor: 'var(--fail-bg)', color: 'var(--fail)', fontWeight: 700 }} />
                  <Chip label={`${notDoneCount} not yet done`} size="small" sx={{ bgcolor: 'var(--neutral-bg)', color: 'var(--neutral)', fontWeight: 700 }} />
                </Box>
              );
            })()}
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Asset No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Inspected</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitAssets.map(asset => {
                  const statusUpper = asset.inspectionStatus?.toUpperCase();
                  const inspectedAfterBookingStart = asset.lastInspectionDate && visitStartDate &&
                    dayjs(asset.lastInspectionDate, 'DD/MM/YYYY').isSameOrAfter(dayjs(visitStartDate), 'day');
                  const doneThisVisit = statusUpper && inspectedAfterBookingStart;
                  return (
                    <TableRow key={asset.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{assetPrimaryLabel(asset)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {[asset.manufacturer, asset.model, asset.item].filter(Boolean).join(' · ') || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{asset.type || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{asset.lastInspectionDate || '—'}</TableCell>
                      <TableCell>
                        {doneThisVisit ? (
                          <StatusChip domain="inspection" value={statusUpper} />
                        ) : (
                          <StatusChip domain="inspection" value="PENDING" label="Not yet done" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : results.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">No inspection records found.</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
              <TableCell sx={{ fontWeight: 600 }}>Asset No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Audit Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Inspector</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reinspection Due</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map(r => {
              const overdue = r.reinspectionDue && dayjs(r.reinspectionDue).isBefore(dayjs(), 'day');
              return (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{assetPrimaryLabel(r) || '—'}</TableCell>
                  <TableCell>{r.customer || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>{auditTypeMap[r.auditType] || r.auditType}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {r.inspectionDate ? dayjs(r.inspectionDate).format('DD/MM/YYYY') : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.inspectedBy || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.achievedScore != null ? Number(r.achievedScore).toFixed(1) + '%' : '—'}</TableCell>
                  <TableCell>
                    {r.overallResult ? (
                      <StatusChip domain="inspection" value={r.overallResult} />
                    ) : <Typography variant="caption" color="text.secondary">Incomplete</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: overdue ? 'error.main' : undefined, fontWeight: overdue ? 700 : 400 }}>
                    {r.reinspectionDue ? dayjs(r.reinspectionDue).format('DD/MM/YYYY') : '—'}
                    {overdue && <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: 10 }}>Overdue</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Generate PDF report">
                      <IconButton size="small" onClick={() => handleGeneratePdf(r)} disabled={generatingPdf === r.id}>
                        {generatingPdf === r.id ? <CircularProgress size={16} /> : <PictureAsPdfIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditRecord(r); setFormOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <InspectionForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
        editRecord={editRecord}
        companies={companies}
      />

      <MissingAssetsDialog open={missingOpen} onClose={() => setMissingOpen(false)} companies={companies} auditTypeMap={auditTypeMap} />
      <AuditSummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} companies={companies} auditTypeMap={auditTypeMap} />
    </Box>
  );
}
